<template>
  <div class="h-screen min-h-screen-safe bg-slate-900 font-sans text-slate-200 overflow-hidden flex flex-col pb-safe">
    
    <header class="flex-none sticky top-0 z-50 bg-[#0B1021]/95 backdrop-blur-md border-b border-slate-800 pt-safe flex flex-col shadow-lg">
      <div class="px-3 py-3 flex justify-between items-center">
        
        <div class="flex items-center gap-2 min-w-0">
          <a 
            href="https://m.lottobox.cc"
            @click.prevent="goBackToMain" 
            class="flex-none inline-flex items-center justify-center w-8 h-8 text-slate-400 hover:text-white transition-colors active:scale-90"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </a>
          <h2 class="font-bold text-[13px] sm:text-[14px] text-slate-200 tracking-wider flex items-center gap-1 uppercase truncate">
            LOTTOBOX <span class="text-red-500">LIVE</span> 4D DRAW
          </h2>
        </div>

        <div class="flex items-center gap-2 flex-none">
          <button @click="toggleLanguage" class="flex-none text-[10px] px-1.5 py-0.5 bg-slate-800 text-slate-300 hover:text-white rounded border border-slate-600 transition-colors active:scale-95 font-bold">
            {{ locale === 'zh' ? 'EN' : '中' }}
          </button>

          <div class="flex items-center gap-1 bg-red-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-[0_0_10px_rgba(220,38,38,0.5)] whitespace-nowrap">
            <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="3" fill="currentColor"></circle>
              <path d="M5 19C2 15.5 2 8.5 5 5"></path>
              <path d="M19 19C22 15.5 22 8.5 19 5"></path>
            </svg>
            <span class="tracking-widest uppercase">{{ locale === 'zh' ? '直播' : 'LIVE' }}</span>
          </div>
        </div>

      </div>
    </header>

    <div class="flex-auto relative w-full h-full overflow-hidden">
      <ClientOnly>
        <template #fallback>
          <div class="flex items-center justify-center h-full text-slate-500 tracking-widest">加载开奖大厅...</div>
        </template>

        <swiper :slides-per-view="1" :space-between="0" :loop="true" class="w-full h-full">
          <swiper-slide v-for="lotto in sortedLotteries" :key="lotto.id">
            <div class="h-full w-full overflow-y-auto px-2 pt-3 pb-16 max-w-md mx-auto">
              
              <div class="relative flex items-end justify-center pb-2 mb-4 border-b-2 border-slate-700 pt-5">
                
                <div class="absolute left-1 bottom-2">
                  <span class="text-[13px] font-bold text-slate-300 tracking-wider">{{ lotto.drawInfo.date }}</span>
                </div>

                <div class="flex flex-col items-center justify-center">
                  <div class="w-14 h-14 bg-white rounded-lg flex items-center justify-center p-1 shadow-md">
                    <img :src="lotto.logo" :alt="$t(`companies.${lotto.code}`)" class="w-full h-full object-contain" />
                  </div>
                  <h1 class="text-base font-extrabold text-white mt-1 tracking-wider">{{ $t(`companies.${lotto.code}`) }}</h1>
                </div>

                <div class="absolute right-1 bottom-2">
                  <span class="text-xs font-bold text-slate-400 tracking-widest">{{ getWeekday(lotto.drawInfo.date, locale) }}</span>
                </div>
                
              </div> 

              <div class="relative w-full flex flex-col gap-3">

	        <div 
                  v-if="lotto.clubId === 4 && isSweepDelayNow" 
                  class="relative z-[60] overflow-hidden bg-red-900/90 border-2 border-red-500/80 rounded-lg p-3 text-center shadow-[0_0_25px_rgba(220,38,38,0.5)] backdrop-blur-md mb-2"
                >
                  <div class="absolute inset-0 bg-gradient-to-r from-red-600/0 via-red-500/30 to-red-600/0 animate-[pulse_2s_ease-in-out_infinite]"></div>
                  
                  <div class="relative z-10 flex flex-col items-center justify-center space-y-1">
                    <div class="flex items-center space-x-2">
                      <span class="text-xl animate-bounce">⏳</span>
                      <span class="text-red-50 font-extrabold text-sm md:text-base leading-snug tracking-wider">
                        {{ locale === 'zh' ? '今天是本月首个星期三（大彩开奖日），新加坡 4D 成绩预计延迟至晚上 7:20 左右公布。' : 'Today is the Big Sweep draw. Singapore 4D results are expected around 7:20 PM.' }}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div v-if="lotto.drawStatus === 'running' && lotto.youtubeUrl && [4, 8, 9].includes(lotto.clubId)" 
                     class="w-full relative bg-black rounded-lg overflow-hidden shadow-lg border border-slate-700 aspect-video z-10 mb-3">
                  <iframe 
                    :src="getIframeUrl(lotto.youtubeUrl)" 
                    class="w-full h-full absolute top-0 left-0" 
                    frameborder="0" 
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                    allowfullscreen>
                  </iframe>
                </div>

                <div class="w-full bg-slate-800 border border-slate-700 rounded-lg overflow-hidden shadow-lg flex flex-col">
                  <div class="flex border-b border-slate-700">
                    <div class="w-1/3 flex items-center justify-center bg-slate-700/30 text-[15px] font-bold text-slate-300 border-r border-slate-700">{{ $t('prize_1st') }}</div>
                    <div class="w-2/3 h-[52px] flex items-center justify-center relative overflow-hidden">
                      <span v-if="lotto.results.isPromotion && lotto.results.prizes.p1 && lotto.results.prizes.p1 !== '----'" class="absolute z-10 top-1 left-2 text-[10px] font-bold text-red-500">{{ lotto.results.prizes.p1Letter }}</span>
                      <Transition name="slot-roll" mode="out-in" :duration="{ enter: 20700, leave: 300 }">
                        <span :key="lotto.results.prizes.p1 || '----'" class="text-2xl font-bold tracking-[0.2em] text-white absolute">{{ lotto.results.prizes.p1 || '----' }}</span>
                      </Transition>
                    </div>
                  </div>
                  <div class="flex border-b border-slate-700">
                    <div class="w-1/3 flex items-center justify-center bg-slate-700/30 text-[15px] font-bold text-slate-300 border-r border-slate-700">{{ $t('prize_2nd') }}</div>
                    <div class="w-2/3 h-[52px] flex items-center justify-center relative overflow-hidden">
                      <span v-if="lotto.results.isPromotion && lotto.results.prizes.p2 && lotto.results.prizes.p2 !== '----'" class="absolute z-10 top-1 left-2 text-[10px] font-bold text-red-500">{{ lotto.results.prizes.p2Letter }}</span>
                      <Transition name="slot-roll" mode="out-in" :duration="{ enter: 20700, leave: 300 }">
                        <span :key="lotto.results.prizes.p2 || '----'" class="text-2xl font-bold tracking-[0.2em] text-white absolute">{{ lotto.results.prizes.p2 || '----' }}</span>
                      </Transition>
                    </div>
                  </div>
                  <div class="flex">
                    <div class="w-1/3 flex items-center justify-center bg-slate-700/30 text-[15px] font-bold text-slate-300 border-r border-slate-700">{{ $t('prize_3rd') }}</div>
                    <div class="w-2/3 h-[52px] flex items-center justify-center relative overflow-hidden">
                      <span v-if="lotto.results.isPromotion && lotto.results.prizes.p3 && lotto.results.prizes.p3 !== '----'" class="absolute z-10 top-1 left-2 text-[10px] font-bold text-red-500">{{ lotto.results.prizes.p3Letter }}</span>
                      <Transition name="slot-roll" mode="out-in" :duration="{ enter: 20700, leave: 300 }">
                        <span :key="lotto.results.prizes.p3 || '----'" class="text-2xl font-bold tracking-[0.2em] text-white absolute">{{ lotto.results.prizes.p3 || '----' }}</span>
                      </Transition>
                    </div>
                  </div>
                </div>

                <LottoGrid :title="$t('specials')" :numbers="lotto.results.specials" :showLetters="lotto.results.isPromotion" :totalSlots="[2, 4, 7].includes(lotto.clubId) ? 10 : 13" :letterStartCode="65" />
                <LottoGrid :title="$t('consolations')" :numbers="lotto.results.consolations" :showLetters="lotto.results.isPromotion" :totalSlots="10" :letterStartCode="78" />

                <div v-if="lotto.results.jackpot4d && lotto.code !== 'nine'" class="w-full bg-[#A21B21] border border-red-800 rounded-lg overflow-hidden shadow-lg flex flex-col items-center justify-center py-3">
                  <div class="text-xl font-bold text-white tracking-widest">Jackpot Pool</div>
                  <div class="text-[22px] font-black text-white tracking-wider mt-0.5">
                    {{ lotto.results.jackpot4d }}
                  </div>
                </div>

                <div v-if="lotto.code === 'gd' || lotto.code === 'nine'" class="space-y-3">
                  
                  <div v-if="lotto.code === 'gd'" class="w-full bg-gradient-to-b from-red-700 to-red-900 border border-red-600 rounded-lg overflow-hidden shadow-lg flex flex-col items-center justify-center py-2">
                    <div class="text-xl font-bold text-white tracking-widest">{{ lotto.results.sixth?.bonusStatus || 'No Bonus' }}</div>
                    <div class="h-[36px] flex items-center justify-center text-2xl font-black text-white tracking-wider my-0.5 w-full">
                      <span>{{ lotto.results.sixth?.bonusLetter || 'K' }} :</span>
                      <div class="relative w-[72px] h-full flex items-center justify-center ml-2">
                        <Transition name="slot-roll" mode="out-in" :duration="{ enter: 20700, leave: 300 }">
                          <span :key="lotto.results.sixth?.bonusNumber || '----'" class="absolute">{{ lotto.results.sixth?.bonusNumber || '----' }}</span>
                        </Transition>
                      </div>
                    </div>
                    <div class="text-xs text-red-200">USD 0.00/unit</div>
                  </div>

                  <div class="w-full bg-slate-800 border border-slate-700 rounded-lg overflow-hidden shadow-lg flex flex-col"
                       :class="lotto.code === 'nine' ? 'border-orange-500/30' : ''">
                    <div class="py-1 text-center border-b"
                         :class="lotto.code === 'nine' ? 'bg-gradient-to-r from-orange-600 to-orange-500 border-orange-500/50' : 'bg-slate-700 border-slate-600'">
                      <h3 class="text-sm font-bold text-white tracking-widest">{{ lotto.code === 'gd' ? 'GD 6D' : '6D' }}</h3>
                    </div>
                    <div class="flex border-b border-slate-700">
                      <div class="w-1/4 flex items-center justify-center bg-slate-700/30 font-bold border-r border-slate-700"
                           :class="lotto.code === 'nine' ? 'text-orange-400' : 'text-slate-300'">1ST</div>
                      <div class="w-3/4 h-[40px] flex items-center justify-center text-lg font-bold tracking-widest text-white relative">
                        <Transition name="slot-roll" mode="out-in" :duration="{ enter: 20700, leave: 300 }">
                          <span :key="lotto.results.sixth?.d6?.p1 || '------'" class="absolute">{{ lotto.results.sixth?.d6?.p1 || '------' }}</span>
                        </Transition>
                      </div>
                    </div>
                    <div v-for="(item, idx) in [
                      { label: '2ND', val1: lotto.results.sixth?.d6?.p2_1, val2: lotto.results.sixth?.d6?.p2_2 },
                      { label: '3RD', val1: lotto.results.sixth?.d6?.p3_1, val2: lotto.results.sixth?.d6?.p3_2 },
                      { label: '4TH', val1: lotto.results.sixth?.d6?.p4_1, val2: lotto.results.sixth?.d6?.p4_2 },
                      { label: '5TH', val1: lotto.results.sixth?.d6?.p5_1, val2: lotto.results.sixth?.d6?.p5_2 }
                    ]" :key="item.label" class="flex" :class="idx !== 3 ? 'border-b border-slate-700' : ''">
                      <div class="w-1/4 flex items-center justify-center bg-slate-700/30 font-bold border-r border-slate-700"
                           :class="lotto.code === 'nine' ? 'text-orange-400' : 'text-slate-300'">{{ item.label }}</div>
                      <div class="w-3/4 h-[40px] flex items-center justify-between px-6 text-base font-bold tracking-wider text-white">
                        <div class="w-2/5 text-left relative h-full flex items-center">
                          <Transition name="slot-roll" mode="out-in" :duration="{ enter: 20700, leave: 300 }">
                            <span :key="item.val1 || '-----'" class="absolute left-0">{{ item.val1 || '-----' }}</span>
                          </Transition>
                        </div>
                        <div class="w-1/5 text-center text-[10px] font-normal" :class="lotto.code === 'nine' ? 'text-orange-500' : 'text-slate-500'">OR</div>
                        <div class="w-2/5 text-right relative h-full flex items-center justify-end">
                          <Transition name="slot-roll" mode="out-in" :duration="{ enter: 20700, leave: 300 }">
                            <span :key="item.val2 || '-----'" class="absolute right-0">{{ item.val2 || '-----' }}</span>
                          </Transition>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div v-if="lotto.code === 'gd'" class="w-full bg-slate-800 border border-slate-700 rounded-lg overflow-hidden shadow-lg flex flex-col">
                    <div class="bg-slate-700 py-1 text-center border-b border-slate-600">
                      <h3 class="text-sm font-bold text-white tracking-widest">6+1D Dragon Jackpot</h3>
                    </div>
                    <div class="flex border-b border-slate-700">
                      <div class="w-1/3 flex items-center justify-center bg-slate-700/30 text-xs font-bold text-slate-300 border-r border-slate-700 text-center px-1">Grand Prize</div>
                      <div class="w-2/3 h-[46px] flex items-center justify-center text-lg font-bold tracking-wider text-white relative">
                        <Transition name="slot-roll" mode="out-in" :duration="{ enter: 20700, leave: 300 }">
                          <span :key="lotto.results.sixth?.d6plus?.grand || 'empty'" class="absolute" v-html="render6Plus1(lotto.results.sixth?.d6plus?.grand, lotto.code)"></span>
                        </Transition>
                      </div>
                    </div>
                    <div class="flex">
                      <div class="w-1/3 flex items-center justify-center bg-slate-700/30 text-xs font-bold text-slate-300 border-r border-slate-700 text-center px-1">Conso Prize</div>
                      <div class="w-2/3 h-[46px] flex items-center justify-center text-lg font-bold tracking-wider text-white relative">
                        <Transition name="slot-roll" mode="out-in" :duration="{ enter: 20700, leave: 300 }">
                          <span :key="lotto.results.sixth?.d6plus?.conso || 'empty'" class="absolute" v-html="render6Plus1(lotto.results.sixth?.d6plus?.conso, lotto.code)"></span>
                        </Transition>
                      </div>
                    </div>
                  </div>

                  <div v-if="lotto.code === 'nine'" class="w-full bg-slate-800 border border-orange-500/40 rounded-lg overflow-hidden shadow-[0_0_15px_rgba(249,115,22,0.1)] flex flex-col">
                    <div class="flex flex-col items-center justify-center py-2 bg-gradient-to-b from-slate-800 to-slate-900 border-b border-orange-500/30">
                       <div class="text-sm font-bold text-orange-400 tracking-widest">6+1D PRIZE</div>
                       <div class="text-xl font-black text-white mt-0.5">{{ lotto.results.sixth?.jackpot || 'USD 0.00' }}</div>
                    </div>
                    <div class="h-[52px] flex items-center justify-center text-[22px] font-bold tracking-[0.25em] text-white relative bg-slate-900">
                      <Transition name="slot-roll" mode="out-in" :duration="{ enter: 20700, leave: 300 }">
                         <span :key="lotto.results.sixth?.d6plus?.grand || 'empty'" class="absolute" v-html="render6Plus1(lotto.results.sixth?.d6plus?.grand, lotto.code)"></span>
                       </Transition>
                    </div>
                  </div>

                  <div v-if="lotto.code === 'gd'" class="w-full bg-[#A21B21] border border-red-800 rounded-lg overflow-hidden shadow-lg flex flex-col items-center justify-center py-3">
                    <div class="text-xl font-bold text-red-200">6+1D Jackpot Pool</div>
                    <div class="text-2xl font-black text-white tracking-wider mt-1">
                      {{ lotto.results.sixth?.jackpot || 'USD 0' }}
                    </div>
                  </div>

                  <div v-if="lotto.code === 'nine' && lotto.results.jackpot4d" class="w-full bg-slate-900 border border-orange-500/40 rounded-lg overflow-hidden shadow-lg flex flex-col mt-2">
                      <div class="flex flex-col items-center justify-center py-3 bg-gradient-to-b from-slate-800 to-slate-900 border-b border-orange-500/30">
                         <div class="text-[13px] font-black text-white tracking-widest">SUPER <span class="text-orange-400 text-lg">JACKPOT</span> pool</div>
                         <div class="text-xl font-bold text-white mt-1">{{ lotto.results.jackpot4d }}</div>
                      </div>
                      <div class="flex flex-col p-2 gap-2">
                         <div class="flex items-center justify-between bg-slate-800/80 p-1.5 rounded border border-slate-700/50">
                           <div class="text-xs font-bold text-orange-400 border border-orange-500/40 px-2 py-0.5 rounded tracking-wide">GRAND PRIZE</div>
                           <div class="text-sm font-bold tracking-widest text-white" v-html="renderSuperJackpot(lotto.results.sixth?.d6plus?.grand, lotto.results.prizes.p1)"></div>
                         </div>
                         <div class="flex items-center justify-between bg-slate-800/80 p-1.5 rounded border border-slate-700/50">
                           <div class="text-xs font-bold text-orange-400 border border-orange-500/40 px-2 py-0.5 rounded tracking-wide">SUPER PRIZE</div>
                           <div class="text-sm font-bold tracking-widest text-white" v-html="renderSuperJackpot(lotto.results.sixth?.d6plus?.grand, lotto.results.prizes.p2)"></div>
                         </div>
                         <div class="flex items-center justify-between bg-slate-800/80 p-1.5 rounded border border-slate-700/50">
                           <div class="text-xs font-bold text-orange-400 border border-orange-500/40 px-2 py-0.5 rounded tracking-wide">MINOR PRIZE</div>
                           <div class="text-sm font-bold tracking-widest text-white" v-html="renderSuperJackpot(lotto.results.sixth?.d6plus?.grand, lotto.results.prizes.p3)"></div>
                         </div>
                      </div>
                  </div>

                </div> 

                <div v-if="lotto.code === 'toto'" class="space-y-3 mt-3">
                  <div class="w-full bg-slate-800 border border-slate-700 rounded-lg overflow-hidden shadow-lg flex flex-col">
                    <div class="bg-slate-700 py-1 text-center border-b border-slate-600">
                      <h3 class="text-sm font-bold text-white tracking-widest">TOTO 5D</h3>
                    </div>
                    <div class="flex flex-wrap">
                      <div v-for="(item, idx) in [
                        { label: $t('prize_1st'), val: lotto.results.fifth?.d5?.p1 },
                        { label: $t('prize_4th'), val: lotto.results.fifth?.d5?.p4 },
                        { label: $t('prize_2nd'), val: lotto.results.fifth?.d5?.p2 },
                        { label: $t('prize_5th'), val: lotto.results.fifth?.d5?.p5 },
                        { label: $t('prize_3rd'), val: lotto.results.fifth?.d5?.p3 },
                        { label: $t('prize_6th'), val: lotto.results.fifth?.d5?.p6 }
                      ]" :key="item.label" class="w-1/2 flex border-b border-slate-700" :class="idx % 2 === 0 ? 'border-r border-slate-700' : ''">
                        <div class="w-1/3 flex items-center justify-center bg-slate-700/30 text-[13px] font-bold text-slate-400 py-2">{{ item.label }}</div>
                        <div class="w-2/3 h-[40px] flex items-center justify-center text-[17px] font-bold tracking-widest text-white relative overflow-hidden">
                          <Transition name="slot-roll" mode="out-in" :duration="{ enter: 20700, leave: 300 }">
                            <span :key="item.val || '-----'" class="absolute">{{ item.val || '-----' }}</span>
                          </Transition>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="w-full bg-slate-800 border border-slate-700 rounded-lg overflow-hidden shadow-lg flex flex-col">
                    <div class="bg-slate-700 py-1 text-center border-b border-slate-600">
                      <h3 class="text-sm font-bold text-white tracking-widest">TOTO 6D</h3>
                    </div>
                    <div class="flex border-b border-slate-700">
                      <div class="w-1/4 flex items-center justify-center bg-slate-700/30 font-bold text-slate-400 border-r border-slate-700">{{ $t('prize_1st') }}</div>
                      <div class="w-3/4 h-[44px] flex items-center justify-center text-2xl font-bold tracking-[0.15em] text-white relative">
                        <Transition name="slot-roll" mode="out-in" :duration="{ enter: 20700, leave: 300 }">
                          <span :key="lotto.results.sixth?.d6?.p1 || '------'" class="absolute">{{ lotto.results.sixth?.d6?.p1 || '------' }}</span>
                        </Transition>
                      </div>
                    </div>
                    <div v-for="(item, idx) in [
                      { label: $t('prize_2nd'), val1: lotto.results.sixth?.d6?.p2_1, val2: lotto.results.sixth?.d6?.p2_2, dash: '-' },
                      { label: $t('prize_3rd'), val1: lotto.results.sixth?.d6?.p3_1, val2: lotto.results.sixth?.d6?.p3_2, dash: '--' },
                      { label: $t('prize_4th'), val1: lotto.results.sixth?.d6?.p4_1, val2: lotto.results.sixth?.d6?.p4_2, dash: '---' },
                      { label: $t('prize_5th'), val1: lotto.results.sixth?.d6?.p5_1, val2: lotto.results.sixth?.d6?.p5_2, dash: '----' }
                    ]" :key="item.label" class="flex" :class="idx !== 3 ? 'border-b border-slate-700' : ''">
                      <div class="w-1/4 flex items-center justify-center bg-slate-700/30 font-bold text-slate-400 border-r border-slate-700">{{ item.label }}</div>
                      <div class="w-3/4 h-[40px] flex items-center justify-between px-6 text-[15px] font-bold tracking-[0.1em] text-white">
                        <div class="w-[45%] text-left relative h-full flex items-center">
                          <Transition name="slot-roll" mode="out-in" :duration="{ enter: 20700, leave: 300 }">
                            <span :key="item.val1 || '------'" class="absolute left-0">{{ item.val1 ? item.val1 + item.dash : '------' }}</span>
                          </Transition>
                        </div>
                        <div class="w-[10%] text-center text-[10px] font-normal text-slate-500 italic">{{ $t('or') }}</div>
                        <div class="w-[45%] text-right relative h-full flex items-center justify-end">
                          <Transition name="slot-roll" mode="out-in" :duration="{ enter: 20700, leave: 300 }">
                            <span :key="item.val2 || '------'" class="absolute right-0">{{ item.val2 ? item.dash + item.val2 : '------' }}</span>
                          </Transition>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div v-if="lotto.youtubeUrl && !isWaitingForDraw(lotto.expectedDrawTime, lotto.results.prizes.p1, lotto.drawStatus)" class="pt-4 pb-8 w-full z-10">
                    <button 
                      v-if="lotto.drawStatus !== 'completed' && !(lotto.drawStatus === 'running' && [4, 8, 9].includes(lotto.clubId))"
                      @click="openLiveStream(lotto.youtubeUrl)"
                      class="w-full text-white font-bold py-3 px-4 rounded-lg flex items-center justify-center gap-2 shadow-lg transition-all active:scale-95"
                      :class="lotto.code === 'nine' ? 'bg-orange-600 hover:bg-orange-500 shadow-orange-900/50' : 'bg-red-600 hover:bg-red-500 shadow-red-900/50'"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4 fill-white"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>
                      {{ $t('watch_live') }}
                    </button>

                    <a 
                      v-else-if="lotto.drawStatus === 'completed'"
                      :href="formatToWatchUrl(lotto.youtubeUrl)"
                      target="_blank"
                      class="w-full text-white font-bold py-3 px-4 rounded-lg flex items-center justify-center gap-2 shadow-lg transition-all active:scale-95"
                      :class="lotto.code === 'nine' ? 'bg-orange-600 hover:bg-orange-500 shadow-orange-900/50' : 'bg-red-600 hover:bg-red-500 shadow-red-900/50'"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4 fill-white"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>
                      {{ $t('watch_replay') }}
                    </a>
                </div>

		<div v-if="isWaitingForDraw(lotto.expectedDrawTime, lotto.results.prizes.p1, lotto.drawStatus)" 
                     class="absolute -inset-x-1 -inset-y-2 z-50 backdrop-blur-md bg-[#0B1021]/85 rounded-xl border border-slate-700/50 flex flex-col items-center justify-start pt-20 shadow-2xl transition-all duration-500">
                  
                  <span class="text-[10px] text-slate-400 font-semibold tracking-[0.15em] uppercase mb-1">
                    {{ locale === 'zh' ? '倒计时' : 'COUNTDOWN' }}
                  </span>
                  <div class="text-3xl font-mono font-bold tracking-widest text-[#fde047] drop-shadow-[0_0_8px_rgba(253,224,71,0.4)] mb-6">
                    {{ getCountdownTimer(lotto.expectedDrawTime) }}
                  </div>

                  <div class="flex items-center gap-3 text-slate-300">
                    <div class="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center shadow-[0_0_15px_rgba(255,255,255,0.05)]">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-slate-400"><path d="M5 22h14"></path><path d="M5 2h14"></path><path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22"></path><path d="M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2"></path></svg>
                    </div>
                    <div class="flex flex-col">
                      <span class="text-[10px] text-slate-400 uppercase tracking-widest leading-none mb-1">Next Draw</span>
                      <span class="text-xl font-black tracking-wider text-white leading-none">{{ lotto.expectedDrawTime }}</span>
                    </div>
                  </div>

                  <p class="text-[11px] text-slate-500 mt-8 tracking-widest uppercase animate-pulse">Waiting for live signal...</p>
                </div> 
                </div> 
	      <AppFooter />
            </div> 
            </swiper-slide>
        </swiper>
      </ClientOnly>
    </div>

    <Transition
      enter-active-class="transition duration-300 ease-out"
      enter-from-class="opacity-0 scale-95"
      enter-to-class="opacity-100 scale-100"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-95"
    >
      <div v-if="activeVideoUrl" class="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm px-4">
        <div class="relative w-full max-w-md aspect-video bg-black rounded-xl overflow-hidden shadow-[0_0_40px_rgba(220,38,38,0.3)] border border-slate-700">
          
          <button @click="closeLiveStream" class="absolute top-safe right-4 z-20 p-1.5 bg-black/60 text-slate-300 rounded-full hover:bg-red-600 hover:text-white transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-6 h-6"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
          </button>
          
          <iframe
            :src="activeVideoUrl"
            class="absolute inset-0 w-full h-full"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowfullscreen
          ></iframe>
        </div>
      </div>
    </Transition>

  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { Swiper, SwiperSlide } from 'swiper/vue'
import 'swiper/css'

import { getInitialLotteries } from '@/constants/lottoTemplates'
import { getWeekday } from '@/utils/formatters'
import { getTodayDrawList } from '@/utils/drawCalendar'

const { t, locale, setLocale } = useI18n()

const seoData = computed(() => {
  const isZh = locale.value === 'zh'
  return {
    title: isZh
      ? 'Live 4D 实时开奖 - 万字与马票成绩 | 新加坡马会现场摇珠直播 | 豪龙、9Lotto | Lottobox'
      : 'Live 4D Results - Singapore Pools Broadcast live draw | GD Lotto & 9Lotto | Lottobox',
    description: isZh
      ? '全网最快马新 4D / 万字 / 马票实时开奖大厅。独家追踪新加坡马会4D现场摇珠直播、豪龙彩票现场摇珠直播、9Lotto现场摇珠直播。提供万能(Magnum)、多多(Toto)、大马彩及东马各家万字票秒速更新成绩。无广告，纯净体验。'
      : 'Fastest Live 4D Results in Malaysia & Singapore. Watch Singapore Pools Broadcast live draw, GD Lotto and 9Lotto live broadcasts. Real-time updates for Magnum4d, Sports Toto, Damacai, Sabah, Sarawak, and Sandakan. No ads, just results.',
    // 🚀 核心词大网加入：万字, 万字票, 万字成绩, 查万字
    keywords: '万字, 万字票, 万字成绩, 查万字, 马票, 查马票, 马票成绩, 马票开奖, Singapore Pools Broadcast live draw, 新加坡马会4D现场摇珠直播, 豪龙彩票现场摇珠直播, 9Lotto现场摇珠直播, 4D result, 4d实时开奖, live 4d, Magnum4d, magnum 4d, toto 4d, sports toto, sportstoto, damacai, Sabah, Sarawak, Sandakan, lottobox'
  }
})

useHead(() => ({
  title: seoData.value.title,
  meta: [
    { name: 'description', content: seoData.value.description },
    { name: 'keywords', content: seoData.value.keywords }
  ],
  script: [
    {
      type: 'application/ld+json',
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": seoData.value.title,
        "description": seoData.value.description,
        "url": "https://live.lottobox.cc",
        "publisher": {
          "@type": "Organization",
          "name": "Lottobox",
          "logo": "https://live.lottobox.cc/favicon.png"
        }
      })
    }
  ]
}))

const toggleLanguage = () => setLocale(locale.value === 'zh' ? 'en' : 'zh')

// 🚀 新增：让 Vue 拥有感知真实时间流逝的能力
const currentTime = ref(new Date())
let clockTimer: ReturnType<typeof setInterval> | null = null

let lastCheckedDateStr = ''
const lotteries = ref<any[]>([])

// 🚀 新增：动态战术排序雷达 (Running > 刚结束5分钟内 > Waiting > Completed)
const sortedLotteries = computed(() => {
  if (!lotteries.value || lotteries.value.length === 0) return [];

  return [...lotteries.value].sort((a, b) => {
    // 1. 权重分配：谁最紧急谁排前面
    const getWeight = (lotto: any) => {
      if (lotto.drawStatus === 'running') return 1;    // 🔥 激战中：置顶 (1)
      
      // 🚨 终极修复：同时识别 'completed' 和 'stop'
      if (lotto.drawStatus === 'completed' || lotto.drawStatus === 'stop') {
        // 🌟 聚光灯时刻：如果有 completedAt 时间戳，计算距离现在过了多久
        if (lotto.completedAt) {
          const timePassed = currentTime.value.getTime() - lotto.completedAt;
          if (timePassed < 5 * 60 * 1000) { 
            // 🏅 刚结束的 5 分钟内：权重设为 1.5！
            return 1.5; 
          }
        }
        return 3;  // 🏁 超过 5 分钟的彻底结束：失去光环，垫底 (3)
      }
      
      return 2; // ⏳ 等待中：居中 (2)
    };

    const weightA = getWeight(a);
    const weightB = getWeight(b);

    if (weightA !== weightB) {
      return weightA - weightB;
    }

    const timeA = a.expectedDrawTime || '23:59';
    const timeB = b.expectedDrawTime || '23:59';
    if (timeA !== timeB) {
      return timeA.localeCompare(timeB);
    }
    
    return a.id - b.id;
  });
})

const goBackToMain = () => {
  if (window.self !== window.top) {
    window.parent.postMessage('closeLiveDraw', '*');
  } else {
    window.location.href = 'https://m.lottobox.cc';
  }
}

const getTodayString = () => {
  const d = new Date()
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

const render6Plus1 = (val: string | null | undefined, brandCode: string) => {
  const plusColor = brandCode === 'nine' ? 'text-orange-500' : 'text-red-500';
  if (!val || val === '----' || val === '------ + -') return `------ <span class="${plusColor} mx-1">+</span> -`;
  const cleanVal = val.replace(/\s+/g, '').replace('+', ''); 
  if (cleanVal.length > 1) {
    return `${cleanVal.slice(0, -1)} <span class="${plusColor} mx-1">+</span> ${cleanVal.slice(-1)}`;
  }
  return val;
}

const renderSuperJackpot = (d6plus: any, p4d: any) => {
  if (!d6plus || d6plus === '----') d6plus = '-------';
  if (!p4d || p4d === '----') p4d = '----';
  
  const cleanD6 = d6plus.replace(/\s+/g, '').replace('+', '');
  let prefix = '------';
  let suffix = '-';
  
  if (cleanD6.length > 1 && cleanD6 !== '-------') {
    prefix = cleanD6.slice(0, -1);
    suffix = cleanD6.slice(-1);
  }

  return `${prefix}<span class="text-orange-500 mx-0.5">+</span>${suffix}<span class="text-orange-500 mx-0.5">+</span>${p4d}`;
}

const initTodayLobby = () => {
  const today = new Date()
  const todayStr = today.toDateString()
  const ymdStr = getTodayString() 
  
  if (todayStr !== lastCheckedDateStr) {
    lastCheckedDateStr = todayStr
    const todayDrawRules = getTodayDrawList() 
    const allTemplates = getInitialLotteries() 
    
    lotteries.value = allTemplates
      .filter(template => todayDrawRules.some(rule => rule.id === template.clubId))
      .map(template => {
        const rule = todayDrawRules.find(r => r.id === template.clubId)
        return { 
          ...template, 
          expectedDrawTime: rule?.drawTime || '19:00',
          drawInfo: { ...template.drawInfo, date: ymdStr },
          drawStatus: '',
	  completedAt: 0,
	  isSweepDelay: false,
          sweepMsgEn: '',
          sweepMsgZh: ''
        }
      })
  }
}

// 🚀 新增：根据每家公司的预计时间，计算动态倒计时 (格式 HH:mm:ss)
const getCountdownTimer = (drawTime: string) => {
  if (!drawTime) return '00:00:00'
  
  const [hours, mins] = drawTime.split(':').map(Number)
  const now = currentTime.value
  
  // 构建当天的目标开奖时间
  const targetTime = new Date(now)
  targetTime.setHours(hours, mins, 0, 0)
  
  // 计算毫秒差
  const diff = targetTime.getTime() - now.getTime()
  
  // 如果时间已过，或者刚刚好，显示全 0
  if (diff <= 0) return '00:00:00'
  
  // 换算时、分、秒
  const h = Math.floor(diff / (1000 * 60 * 60))
  const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
  const s = Math.floor((diff % (1000 * 60)) / 1000)
  
  // 补零格式化
  const pad = (num: number) => String(num).padStart(2, '0')
  return `${pad(h)}:${pad(m)}:${pad(s)}`
}

// 🚀 新增：纯前端大彩日推算雷达
const isSweepDelayNow = computed(() => {
  const d = currentTime.value;
  const isFirstWednesday = d.getDay() === 3 && d.getDate() <= 7;
  if (!isFirstWednesday) return false;

  const currentMins = d.getHours() * 60 + d.getMinutes();
  const startMins = 18 * 60 + 30; // 6:30 PM 开始挂出免战牌
  const endMins = 19 * 60 + 20;   // 🚀 延长到 7:20 PM 才撤掉横幅！
  
  return currentMins >= startMins && currentMins < endMins;
});

// 🚀 核心更新：使用 currentTime 实时响应，无需刷新即可自动扯掉遮罩！
const isWaitingForDraw = (drawTime: string, p1Value: string, status?: string) => {
  if (status === 'running') return false; 
  if (p1Value && p1Value !== '----') return false; 
  if (!drawTime) return false;
  
  const [hours, mins] = drawTime.split(':').map(Number);
  
  // 使用每秒跳动的 currentTime，确保到了时间瞬间撤除遮罩
  const currentMins = currentTime.value.getHours() * 60 + currentTime.value.getMinutes();
  const targetMins = hours * 60 + mins;
  
  return currentMins < targetMins;
}

const isDrawTime = () => {
  const currentMins = currentTime.value.getHours() * 60 + currentTime.value.getMinutes()
  const startMins = 18 * 60 + 25 // 6:25 PM 开始监听
  
  const endMins = 22 * 60 + 15    
  
  return currentMins >= startMins && currentMins <= endMins
}

const hasRunningLive = computed(() => {
  if (lotteries.value.some(l => l.drawStatus === 'running')) return true;
  const hasData = lotteries.value.some(l => l.drawStatus);
  const allCompleted = lotteries.value.every(l => !l.drawStatus || l.drawStatus === 'completed');
  if (hasData && allCompleted) return false;
  return isDrawTime();
})

const activeVideoUrl = ref<string | null>(null)

const openLiveStream = (url: string) => { 
  let embedUrl = url;
  if (url.includes('watch?v=')) {
    const videoId = url.split('v=')[1].split('&')[0];
    embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1`;
  } else if (!url.includes('autoplay=1')) {
    embedUrl += url.includes('?') ? '&autoplay=1' : '?autoplay=1';
  }
  activeVideoUrl.value = embedUrl;
}

const closeLiveStream = () => {
  activeVideoUrl.value = null;
}

const formatToWatchUrl = (url: string) => {
  if (!url) return '#';
  if (url.includes('embed/')) {
    const videoId = url.split('embed/')[1].split('?')[0];
    return `https://www.youtube.com/watch?v=${videoId}`;
  }
  return url;
}

const getIframeUrl = (url: string) => {
  if (!url) return '';
  let embedUrl = url;
  
  if (url.includes('watch?v=')) {
    const videoId = url.split('v=')[1].split('&')[0];
    embedUrl = `https://www.youtube.com/embed/${videoId}?playsinline=1`;
  } else if (!url.includes('playsinline=1')) {
    embedUrl += url.includes('?') ? '&playsinline=1' : '?playsinline=1';
  }
  
  if (!embedUrl.includes('autoplay=1')) embedUrl += '&autoplay=1';
  if (!embedUrl.includes('mute=1')) embedUrl += '&mute=1';
  
  return embedUrl;
}

let pollingTimer: ReturnType<typeof setInterval> | null = null

const stabilizeArray = (oldArr: string[], newArr: string[], maxSlots: number) => {
  let merged = [...(oldArr || [])];
  while (merged.length < maxSlots) merged.push('----'); // 填满占位符
  
  let unassigned = [...newArr];
  
  // 1. 检查已有的号码，如果它还在最新名单里，就把它从待分配名单里划掉（保留原位）
  merged.forEach((val, idx) => {
    if (val !== '----' && val !== '') {
      const matchIdx = unassigned.indexOf(val);
      if (matchIdx !== -1) {
        unassigned.splice(matchIdx, 1); // 划掉
      } else {
        merged[idx] = '----'; // 官方纠错：如果最新名单里没有这个号了，清空该坑位
      }
    }
  });
  
  // 2. 把剩下的新号码，挨个填入剩下的空坑位里
  unassigned.forEach(item => {
    const emptyIdx = merged.indexOf('----');
    if (emptyIdx !== -1) {
      merged[emptyIdx] = item;
    }
  });
  
  return merged;
};

const fetchLiveResults = async (isInitialLoad = false) => {
  try {
    initTodayLobby()
    if (!isInitialLoad && !isDrawTime()) return; 

    const response: any = await $fetch(`/api/get-live?t=${Date.now()}`)
    
    if (response.status === 'success' && response.data) {
      const processedSet = new Set();
      const todayYmd = getTodayString();

      response.data.forEach((row: any) => {
        if (row.draw_date !== todayYmd) return;

        const uniqueKey = `${row.club_id}_${row.game_type}`;
        if (processedSet.has(uniqueKey)) return;
        processedSet.add(uniqueKey);

        const target = lotteries.value.find(l => l.clubId === row.club_id)
        if (!target) return;

	if (row.status) {
          const isFinished = row.status === 'completed' || row.status === 'stop';
          
          if (isFinished) {
            // 🚨 终极绝杀逻辑：只要是完成状态，且免死金牌还没贴上去
            if (!target.completedAt) {
              // 如果是刚打开网页(初始加载)，直接作废(设为0)；如果是看着它开完的，立刻贴上当前时间戳！
              target.completedAt = isInitialLoad ? 0 : Date.now();
            }
            target.drawStatus = 'completed';
          } else {
            target.drawStatus = 'running';
            target.completedAt = 0; // 只要还在激战，金牌清零
          }
        }

        let metaObj = null;
        if (row.meta) {
          try { metaObj = typeof row.meta === 'string' ? JSON.parse(row.meta) : row.meta; } 
          catch (e) { console.warn('Meta parse error'); }
        }

        if (row.game_type === '4D') {
          target.results.jackpot4d = row.jackpot_pool || '';

          if (row.youtube_url) {
            let yUrl = row.youtube_url;
            if (yUrl.includes('watch?v=')) {
              yUrl = yUrl.replace('watch?v=', 'embed/') + '?autoplay=1';
            }
            target.youtubeUrl = yUrl;
          }

          target.results.prizes.p1 = (row.first && row.first[0]) ? String(row.first[0]) : '----';
          target.results.prizes.p2 = (row.second && row.second[0]) ? String(row.second[0]) : '----';
          target.results.prizes.p3 = (row.third && row.third[0]) ? String(row.third[0]) : '----';

	  if (metaObj && metaObj.special_letters) {
             const specs = [];
             for(let i = 0; i < 13; i++) { 
                const letter = String.fromCharCode(65 + i); 
                specs.push(metaObj.special_letters[letter] || '----'); // 确保空位是 ----
             }
             target.results.specials = specs;

	  } else {
             if (row.fourth && row.fourth.length > 0) {
                // 🚨 丢弃 stabilizeArray！数据库传来的已经是完美排好序的数组，直接对号入座！
                const maxSlots = [2, 4, 7].includes(target.clubId) ? 10 : 13;
                const newSpecs = [];
                for (let i = 0; i < maxSlots; i++) {
                   newSpecs.push(row.fourth[i] ? String(row.fourth[i]) : '----');
                }
                target.results.specials = newSpecs;
             }
          }

          if (metaObj && metaObj.consolation_letters) {
             const consos = [];
             for(let i = 0; i < 10; i++) { 
                const letter = String.fromCharCode(78 + i); 
                consos.push(metaObj.consolation_letters[letter] || '----');
             }
             target.results.consolations = consos;
          } else {
             if (row.fifth && row.fifth.length > 0) {
                // 🚨 安慰奖也一样，直接按索引拿！
                const newConsos = [];
                for (let i = 0; i < 10; i++) {
                   newConsos.push(row.fifth[i] ? String(row.fifth[i]) : '----');
                }
                target.results.consolations = newConsos;
             }
          }

          if (metaObj) {
             target.results.prizes.p1Letter = metaObj.p1_letter || '';
             target.results.prizes.p2Letter = metaObj.p2_letter || '';
             target.results.prizes.p3Letter = metaObj.p3_letter || '';

	     // 🚀 新增：拦截大彩延迟信号与双语文案
             target.isSweepDelay = metaObj.is_sweep_delay === true;
             target.sweepMsgEn = metaObj.sweep_message_en || '';
             target.sweepMsgZh = metaObj.sweep_message_zh || '';

             if (target.results.sixth) {
               target.results.sixth.bonusStatus = metaObj.bonus_status || 'Todays Bonus';
               target.results.sixth.bonusLetter = metaObj.bonus_letter || 'K';
               if (metaObj.bonus_letter && metaObj.special_letters && metaObj.special_letters[metaObj.bonus_letter]) {
                 target.results.sixth.bonusNumber = metaObj.special_letters[metaObj.bonus_letter];
               }
             }
          }
	} 
	else if (row.game_type === '5D') {
          // 强制初始化，防止 Vue 响应式丢失
          if (!target.results.fifth) target.results.fifth = { d5: {} };
          if (!target.results.fifth.d5) target.results.fifth.d5 = {};
          
          // 🚨 终极防弹解析器：如果是数组就拿 [0]，如果是字符串就直接用，并把多余的引号洗掉！
          const getVal = (val) => Array.isArray(val) ? String(val[0]) : String(val || '').replace(/["']/g, '');

          target.results.fifth.d5.p1 = getVal(row.first);
          target.results.fifth.d5.p2 = getVal(row.second);
          target.results.fifth.d5.p3 = getVal(row.third);
          target.results.fifth.d5.p4 = getVal(row.fourth);
          target.results.fifth.d5.p5 = getVal(row.fifth);
          target.results.fifth.d5.p6 = getVal(row.sixth);
        }
        else if (row.game_type === '6D') {
          // 🚨 核心修复：把 && target.results.sixth 拿掉！因为 Toto 默认没有 sixth，必须强行注入！
          if (!target.results.sixth) target.results.sixth = { d6: {}, d6plus: {} };
          if (!target.results.sixth.d6) target.results.sixth.d6 = {};
          
          target.results.sixth.d6.p1 = (row.first && row.first[0]) ? String(row.first[0]) : '';
          target.results.sixth.d6.p2_1 = (row.second && row.second[0]) ? String(row.second[0]) : '';
          target.results.sixth.d6.p2_2 = (row.second && row.second[1]) ? String(row.second[1]) : '';
          target.results.sixth.d6.p3_1 = (row.third && row.third[0]) ? String(row.third[0]) : '';
          target.results.sixth.d6.p3_2 = (row.third && row.third[1]) ? String(row.third[1]) : '';
          target.results.sixth.d6.p4_1 = (row.fourth && row.fourth[0]) ? String(row.fourth[0]) : '';
          target.results.sixth.d6.p4_2 = (row.fourth && row.fourth[1]) ? String(row.fourth[1]) : '';
          target.results.sixth.d6.p5_1 = (row.fifth && row.fifth[0]) ? String(row.fifth[0]) : '';
          target.results.sixth.d6.p5_2 = (row.fifth && row.fifth[1]) ? String(row.fifth[1]) : '';
        }
        else if (row.game_type === '6+1D' && target.results.sixth) {
          target.results.sixth.d6plus.grand = (row.first && row.first[0]) ? String(row.first[0]) : '';
          target.results.sixth.d6plus.conso = (row.second && row.second[0]) ? String(row.second[0]) : '';
          target.results.sixth.jackpot = row.jackpot_pool || '';
        }
      })
    }
  } catch (error) {
    console.error('拉取实时数据失败:', error)
  }
}

let dynamicPollingTimer: ReturnType<typeof setTimeout> | null = null;

const startSmartPolling = () => {
  const executePoll = async () => {
    // 1. 获取当前是否处于黄金开奖时间 (18:25 - 21:15)
    const inDrawTime = isDrawTime();
    
    // 2. 只有在开奖时间内，才真正去向后端请求数据
    if (inDrawTime) {
        await fetchLiveResults(false);
    }

    // 3. 🧠 智能决定下一次醒来的时间
    let nextInterval = 0;

    if (inDrawTime) {
        // 在开奖时间段内，看看有没有谁正在激战 (running)
        const isAnyRunning = lotteries.value.some(l => l.drawStatus === 'running');
        if (isAnyRunning) {
            nextInterval = 5000;   // 🔥 激战状态：5秒极速雷达！
        } else {
            nextInterval = 30000;  // ⏳ 待命状态：30秒常规巡逻
        }
    } else {
        // 💤 休战时间 (6:30pm 前，9:00pm 后)：进入深度睡眠，10分钟才检查一次时间
        nextInterval = 10 * 60 * 1000; 
    }
    
    // 设置下一次任务
    dynamicPollingTimer = setTimeout(executePoll, nextInterval);
  };

  // 第一次挂载后，等 5 秒钟启动智能循环
  dynamicPollingTimer = setTimeout(executePoll, 5000);
};

onMounted(() => {
  fetchLiveResults(true); // 初始强行拉取一次当日数据
  startSmartPolling();    // 🚀 启动带睡眠模式的雷达
  
  // 钟表脉搏保留，用于前端倒计时显示
  clockTimer = setInterval(() => {
    currentTime.value = new Date()
  }, 1000);
})

onBeforeUnmount(() => {
  if (dynamicPollingTimer) clearTimeout(dynamicPollingTimer);
  if (clockTimer) clearInterval(clockTimer);
})
</script>

<style>
.slot-roll-enter-active { 
  animation: 
    slot-explode 0.7s cubic-bezier(0.175, 0.885, 0.32, 1.275) both,
    alert-flash 0.8s ease-in-out 0.7s 25 both; 
}

.slot-roll-leave-active { 
  animation: slot-leave 0.3s ease-in both; 
  position: absolute; 
}

@keyframes slot-explode {
  0% { 
    opacity: 0; 
    transform: translateY(80%) scale(0.5); 
    color: #ef4444; 
    text-shadow: 0 0 0px transparent; 
  }
  40% { 
    opacity: 1; 
    transform: translateY(-10%) scale(1.3); 
    color: #fbbf24; 
    text-shadow: 0 0 25px rgba(251, 191, 36, 1), 0 0 40px rgba(239, 68, 68, 0.8); 
  }
  100% { 
    opacity: 1; 
    transform: translateY(0) scale(1); 
    color: white; 
    text-shadow: 0 0 10px rgba(255, 255, 255, 0.3); 
  }
}

/* 🚨 60秒高强度抓眼球闪烁特效 */
@keyframes alert-flash {
  0%, 100% {
    color: white;
    text-shadow: 0 0 10px rgba(255, 255, 255, 0.3);
    transform: scale(1);
    background-color: transparent;
    box-shadow: none;
  }
  50% {
    color: #ffea00; 
    text-shadow: 0 0 10px #dc2626, 0 0 20px #dc2626, 0 0 40px #dc2626; 
    transform: scale(1.15); 
    background-color: rgba(220, 38, 38, 0.3); 
    box-shadow: 0 0 15px rgba(220, 38, 38, 0.6); 
    border-radius: 6px;
    padding: 0 8px; 
  }
}

@keyframes slot-leave {
  0% { opacity: 1; transform: translateY(0); }
  100% { opacity: 0; transform: translateY(-100%); }
}
</style>
